import { api } from "./api";

function localTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export async function fetchMySchools(role) {
  const params = role ? { role } : {};
  const { data } = await api.get("/me/schools", { params });
  return data;
}

const MY_SCHOOL_ROLE_RANK = { student: 0, instructor: 1, admin: 2 };

/**
 * Flatten GET /me/schools into [{ id, name, slug, role }].
 * Staff memberships win over following the same school.
 */
export function normalizeMySchoolsList({ managing = [], teaching = [], following = [] } = {}) {
  const byId = new Map();

  function upsert(school, role) {
    if (!school?.id || !school?.slug || !school?.name) return;
    const existing = byId.get(school.id);
    if (!existing || MY_SCHOOL_ROLE_RANK[role] > MY_SCHOOL_ROLE_RANK[existing.role]) {
      byId.set(school.id, {
        id: school.id,
        name: school.name,
        slug: school.slug,
        role,
      });
    }
  }

  for (const school of managing) upsert(school, "admin");
  for (const school of teaching) upsert(school, "instructor");
  for (const school of following) upsert(school, "student");

  return [
    ...managing.map((s) => byId.get(s.id)).filter(Boolean),
    ...teaching.map((s) => byId.get(s.id)).filter((s) => s?.role === "instructor"),
    ...following.map((s) => byId.get(s.id)).filter((s) => s?.role === "student"),
  ].filter((school, index, list) => list.findIndex((item) => item.id === school.id) === index);
}

export async function fetchSchool(idOrSlug, options = {}) {
  const params = {};
  const include = [];
  if (options.includeCalendar) include.push("calendar");
  if (options.includeInstances) include.push("instances");
  if (include.length) params.include = include.join(",");
  if (options.timezone) params.timezone = options.timezone;
  else if (options.includeCalendar) params.timezone = localTimeZone();
  const { data } = await api.get(`/schools/${idOrSlug}`, { params });
  return data;
}

export async function createSchool(payload) {
  const { data } = await api.post("/schools", payload);
  return data.school;
}

export async function updateSchool(schoolId, payload) {
  const { data } = await api.patch(`/schools/${schoolId}`, payload);
  return data.school;
}

export async function fetchSchoolPeople(schoolId) {
  const { data } = await api.get(`/schools/${schoolId}/people`);
  return data;
}

export async function followSchool(schoolId) {
  await api.post(`/schools/${schoolId}/follow`);
}

export async function unfollowSchool(schoolId) {
  await api.delete(`/schools/${schoolId}/follow`);
}

export async function submitPayment(payload) {
  const { data } = await api.post("/payments", payload);
  return data.payment;
}

export async function reviewPayment(paymentId, payload) {
  const { data } = await api.post(`/payments/${paymentId}/review`, payload);
  return data;
}

export async function fetchPaymentQueue(schoolId, status = "PENDING") {
  const { data } = await api.get(`/schools/${schoolId}/payments`, { params: { status } });
  return data.payments;
}

export async function startSchoolOnboarding(schoolId) {
  const { data } = await api.post(`/schools/${schoolId}/onboarding/start`);
  return data.school;
}

export async function advanceSchoolOnboarding(schoolId) {
  const { data } = await api.post(`/schools/${schoolId}/onboarding/advance`);
  return data.school;
}

export async function seedOnboardingAfterProduct(schoolId) {
  const { data } = await api.post(`/schools/${schoolId}/onboarding/seed-after-product`);
  return data;
}

export async function seedOnboardingFeeRequests(schoolId) {
  const { data } = await api.post(`/schools/${schoolId}/onboarding/seed-fee-requests`);
  return data;
}

export async function cleanupSchoolOnboarding(schoolId) {
  const { data } = await api.post(`/schools/${schoolId}/onboarding/cleanup`);
  return data.school;
}

export async function completeSchoolOnboarding(schoolId) {
  const { data } = await api.post(`/schools/${schoolId}/onboarding/complete`);
  return data.school;
}

export async function grantEntitlement(schoolId, payload) {
  const { data } = await api.post(`/schools/${schoolId}/grants`, payload);
  return data.entitlement;
}

export async function adjustCredits(schoolId, payload) {
  const { data } = await api.post(`/schools/${schoolId}/credit-adjust`, payload);
  return data;
}

export async function fetchPersonDetail(schoolId, organizerId) {
  const { data } = await api.get(`/schools/${schoolId}/people/${organizerId}`);
  return data;
}

export async function updatePersonNote(schoolId, organizerId, note) {
  const { data } = await api.patch(`/schools/${schoolId}/people/${organizerId}`, { note });
  return data;
}

export async function fetchInstanceRsvpEligibility(instanceId) {
  const { data } = await api.get(`/instances/${instanceId}/rsvp-eligibility`);
  return data;
}

export async function fetchInstanceRsvps(instanceId) {
  const { data } = await api.get(`/instances/${instanceId}/rsvps`);
  return data;
}

export async function rsvpToInstance(instanceId, payload = {}) {
  const { data } = await api.post(`/instances/${instanceId}/rsvp`, payload);
  return data;
}

export async function cancelClassRsvp(rsvpId, payload = {}) {
  const { data } = await api.post(`/class-rsvps/${rsvpId}/cancel`, payload);
  return data;
}

export async function adminCancelClassRsvp(rsvpId, payload = {}) {
  const { data } = await api.post(`/class-rsvps/${rsvpId}/admin-cancel`, payload);
  return data;
}

export async function fetchSchoolActionItems(schoolId, status = "OPEN") {
  const { data } = await api.get(`/schools/${schoolId}/action-items`, { params: { status } });
  return data;
}

export async function fetchDoorQueue(schoolId) {
  const { data } = await api.get(`/schools/${schoolId}/door-queue`);
  return data;
}

export async function markInstanceAttendance(instanceId, studentOrganizerId, classRsvpId) {
  const body = {};
  if (classRsvpId) body.class_rsvp_id = classRsvpId;
  if (studentOrganizerId) body.student_organizer_id = studentOrganizerId;
  const { data } = await api.post(`/instances/${instanceId}/attendance`, body);
  return data;
}

export async function cancelInstance(instanceId) {
  await api.post(`/instances/${instanceId}/cancel`);
}

export async function markInstanceNoShow(instanceId, studentOrganizerId, resolution) {
  const { data } = await api.post(`/instances/${instanceId}/no-show`, {
    student_organizer_id: studentOrganizerId,
    resolution,
  });
  return data;
}

export async function resolveSchoolActionItem(schoolId, itemId, resolution) {
  const { data } = await api.post(`/schools/${schoolId}/action-items/${itemId}/resolve`, {
    resolution,
  });
  return data.action_item;
}

export async function hostAddStudent(instanceId, payload) {
  const { data } = await api.post(`/instances/${instanceId}/host-rsvp`, payload);
  return data.rsvp;
}

export async function updateClassRsvp(rsvpId, payload) {
  const { data } = await api.patch(`/class-rsvps/${rsvpId}`, payload);
  return data.rsvp;
}

export async function fetchClass(classId) {
  const { data } = await api.get(`/classes/${classId}`, {
    params: { timezone: localTimeZone() },
  });
  return data;
}

export async function resolveClassByPath(schoolSlug, classSlug) {
  const { data } = await api.get(
    `/classes/by-path/${encodeURIComponent(schoolSlug)}/${encodeURIComponent(classSlug)}`,
  );
  return data;
}

export async function followClass(classId) {
  const { data } = await api.post(`/classes/${classId}/follow`);
  return data;
}

export async function unfollowClass(classId) {
  const { data } = await api.delete(`/classes/${classId}/follow`);
  return data;
}

export async function fetchClassFollowers(classId, { instanceId } = {}) {
  const { data } = await api.get(`/classes/${classId}/followers`, {
    params: instanceId ? { instance: instanceId } : undefined,
  });
  return data;
}

export async function promoteEventCoAdmin(eventId, { organizerId, dancerId }) {
  const { data } = await api.post(`/events/${eventId}/admins`, {
    organizer_id: organizerId,
    dancer_id: dancerId,
  });
  return data.admin;
}

export async function removeEventCoAdmin(eventId, organizerId) {
  await api.delete(`/events/${eventId}/admins/${organizerId}`);
}

export async function fetchSchoolNotifications() {
  const { data } = await api.get("/school-notifications");
  return data.notifications;
}

export async function createVenue(schoolId, payload) {
  const { data } = await api.post(`/schools/${schoolId}/venues`, payload);
  return data.venue;
}

export async function updateVenue(schoolId, venueId, payload) {
  const { data } = await api.patch(`/schools/${schoolId}/venues/${venueId}`, payload);
  return data.venue;
}

export async function deleteVenue(schoolId, venueId) {
  await api.delete(`/schools/${schoolId}/venues/${venueId}`);
}

export async function createClass(schoolId, payload) {
  const { data } = await api.post(`/schools/${schoolId}/classes`, {
    ...payload,
    timezone: localTimeZone(),
  });
  return data.class;
}

export async function updateClass(schoolId, classId, payload) {
  const { data } = await api.patch(`/schools/${schoolId}/classes/${classId}`, {
    ...payload,
    timezone: localTimeZone(),
  });
  return data.class;
}

export async function deleteClass(schoolId, classId) {
  await api.delete(`/schools/${schoolId}/classes/${classId}`);
}

export async function updateInstance(instanceId, payload) {
  const { data } = await api.patch(`/instances/${instanceId}`, {
    ...payload,
    timezone: localTimeZone(),
  });
  return data.instance;
}

export async function createProduct(schoolId, payload) {
  const { data } = await api.post(`/schools/${schoolId}/products`, payload);
  return data.product;
}

export async function updateProduct(schoolId, productId, payload) {
  const { data } = await api.patch(`/schools/${schoolId}/products/${productId}`, payload);
  return data.product;
}

export async function deleteProduct(schoolId, productId) {
  await api.delete(`/schools/${schoolId}/products/${productId}`);
}

export function schoolInviteUrl(slug) {
  if (typeof window === "undefined") return `/school/${slug}`;
  return `${window.location.origin}/school/${slug}`;
}
